import type { SessionData } from '../types';

// Client wrappers for the /api/designs D1-backed endpoints.

export interface DesignSummary {
  id: string;
  name: string;
  address: string;
  updated_at: string;
  thumbnail: string | null;
}

export interface SavedDesign {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  roof_model: SessionData['roof_model'];
  panel_layout: SessionData['panel_layout'];
  production: SessionData['production'];
  consumption: number | null;
  module_id: string;
  thumbnail: string | null;
  schema_version: number;
  created_at: string;
  updated_at: string;
}

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  const body = await resp.json().catch(() => null);
  if (!resp.ok || !body?.ok) {
    throw new Error(body?.error || `Request failed (${resp.status})`);
  }
  return body as T;
}

export async function listDesigns(): Promise<DesignSummary[]> {
  const body = await apiFetch<{ designs: DesignSummary[] }>('/api/designs');
  return body.designs;
}

export async function createDesign(
  data: SessionData,
  moduleId: string,
  name: string,
  thumbnail: string | null
): Promise<string> {
  const body = await apiFetch<{ id: string }>('/api/designs', {
    method: 'POST',
    body: JSON.stringify({
      name,
      address: data.address,
      latitude: data.latitude,
      longitude: data.longitude,
      roof_model: data.roof_model,
      panel_layout: data.panel_layout,
      production: data.production,
      consumption: data.consumption,
      module_id: moduleId,
      thumbnail,
    }),
  });
  return body.id;
}

export async function updateDesign(
  id: string,
  data: SessionData,
  moduleId: string,
  thumbnail: string | null
): Promise<void> {
  await apiFetch(`/api/designs/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      address: data.address,
      latitude: data.latitude,
      longitude: data.longitude,
      roof_model: data.roof_model,
      panel_layout: data.panel_layout,
      production: data.production,
      consumption: data.consumption,
      module_id: moduleId,
      thumbnail,
    }),
  });
}

export async function renameDesign(id: string, name: string): Promise<void> {
  await apiFetch(`/api/designs/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export async function getDesign(id: string): Promise<SavedDesign> {
  const body = await apiFetch<{ design: SavedDesign }>(`/api/designs/${id}`);
  return body.design;
}

export async function deleteDesign(id: string): Promise<void> {
  await apiFetch(`/api/designs/${id}`, { method: 'DELETE' });
}
